'use client';

import {
  AdjustmentsHorizontalIcon,
  CalendarDaysIcon,
  ChevronLeftIcon,
  CurrencyEuroIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { type MouseEvent, useMemo, useState } from 'react';
import type { SimpleHeaderProps } from '@/app/_components/layouts/header/types';
import { FiltersFormModal } from '@/app/_components/widgets/listing/listingFilters/filtersModal';
import { useRouter } from 'next/navigation';
import { ROUTES } from '@/routes';
import HeaderComponent from '@/app/_components/layouts/header/_components/headerComponent';
import clsx from 'clsx';
import { ELTagButton, ElOrderItem } from '@davincihealthcare/elty-design-system-react';
import { useListingFilters } from '@/app/_hooks/useListingFilters';
import { getLabelForFilter } from '@/app/_components/widgets/listing/listingFilters/utils';
import { XMarkIcon } from '@heroicons/react/24/solid';

type ListingHeaderLeftSlotProps = Partial<SimpleHeaderProps> & {
  onBackClick?: () => void;
};

const ListingHeaderLeftSlot = ({ onBackClick }: ListingHeaderLeftSlotProps) => {
  const router = useRouter();

  const handleBack = () => {
    if (typeof onBackClick === 'function') {
      onBackClick();
      return;
    }

    if (window.history.length > 1) {
      router.back();
    } else {
      router.push(ROUTES.HOME.pathname);
    }
  };

  return (
    <button className="size-8 cursor-pointer p-1" aria-label="Indietro" onClick={handleBack}>
      <ChevronLeftIcon />
    </button>
  );
};

type ListingHeaderRightSlotProps = SimpleHeaderProps & {
  onFiltersChange?: () => void;
};

const ListingHeaderRightSlot = ({ openSearch, onFiltersChange }: ListingHeaderRightSlotProps) => {
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  return (
    <div className="flex flex-row gap-4">
      <button className="size-8 shrink-0 p-1" aria-label="Cerca" onClick={openSearch}>
        <MagnifyingGlassIcon />
      </button>

      <button className="size-8 shrink-0 p-1" aria-label="Filtri" onClick={() => setIsFiltersOpen(true)}>
        <AdjustmentsHorizontalIcon />
      </button>

      {isFiltersOpen && <FiltersFormModal onClose={() => setIsFiltersOpen(false)} onFiltersChange={onFiltersChange} />}
    </div>
  );
};

type ListingHeaderProps = {
  service?: string;
  location?: string;
  onBackClick?: () => void;
  onActiveFilterRemove?: () => void;
  mapMarkerLabelType?: 'price' | 'firstAvailability';
  setMapMarkerLabelType?: (type: 'price' | 'firstAvailability') => void;
};

const ListingHeader: React.FC<ListingHeaderProps> = ({
  service,
  location,
  onBackClick,
  onActiveFilterRemove,
  mapMarkerLabelType,
  setMapMarkerLabelType,
}) => {
  const { filters, applyFilter } = useListingFilters();
  const activeFilters = useMemo(
    () =>
      (Object.entries(filters) as [keyof typeof filters, (typeof filters)[keyof typeof filters]][])
        .filter(([key]) => key !== 'order')
        .filter(([, value]) => (Array.isArray(value) ? value.length > 0 : !!value)),
    [filters],
  );

  const removeActiveFilter = (event: MouseEvent<HTMLButtonElement>, filterKey: keyof typeof filters) => {
    event.preventDefault();
    event.stopPropagation();

    onActiveFilterRemove?.();
    applyFilter(filterKey);
  };

  return (
    <>
      <HeaderComponent
        type="page-heading"
        showSearchbar
        keepQueryParams
        defaultService={service}
        defaultLocation={location}
        hideOnScroll={false}
        simpleHeaderProps={{
          title: service || 'Cerca',
          subtitle: location,
          slots: {
            left: () => <ListingHeaderLeftSlot onBackClick={onBackClick} />,
            right: props => <ListingHeaderRightSlot {...props} onFiltersChange={onActiveFilterRemove} />,
          },
        }}
        searchProps={{
          openModalOnMobile: false,
        }}
      />

      <div className="z-10 flex flex-col gap-4 bg-neutral-surface p-4 shadow-xl">
        {activeFilters.length > 0 && (
          <div className="flex flex-col gap-2">
            <div className="text-xs font-semibold text-neutral-darker">Filtri attivi</div>

            <div className="-mx-4 flex flex-row gap-2 overflow-x-auto text-nowrap px-4 scrollbar-hide [&>button]:shrink-0 [&>button]:border-none">
              {activeFilters.map(([key, value]) => (
                <ELTagButton
                  key={key}
                  state="focused"
                  label={getLabelForFilter(key, value)}
                  trailingIcon={<XMarkIcon />}
                  onClick={event => removeActiveFilter(event, key)}
                />
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-col gap-2">
          <div className="text-xs font-semibold text-neutral-darker">Risultati nella mappa</div>

          <div className="flex flex-row items-center justify-between">
            <ElOrderItem
              selected={mapMarkerLabelType === 'price'}
              headline={<CurrencyEuroIcon className="size-4" />}
              label="Mostra prezzi"
              labelUnderlined={false}
              size="small"
              className={clsx('!h-[52px] w-full justify-center rounded-l-lg')}
              onClick={() => setMapMarkerLabelType?.('price')}
            />

            <ElOrderItem
              selected={mapMarkerLabelType === 'firstAvailability'}
              headline={<CalendarDaysIcon className="size-4" />}
              label="Mostra disponibilità"
              labelUnderlined={false}
              size="small"
              className={clsx('!h-[52px] w-full justify-center rounded-r-lg')}
              onClick={() => setMapMarkerLabelType?.('firstAvailability')}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default ListingHeader;
